import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import { MapOverlays } from '@openlayers/ol-overlays';
import { declusterFeature, isCluster, setMapCursor } from '@openlayers/ol-utils';
import { openMapToolContextMenu } from '@pages/map/tools/tool-context-menu';
import { NearbyModelsDialog, NearbyModelsService } from '@pages/map/ui/dialog/NearbyModels';
import { GanttPopup } from '@pages/schedule/GanttPopup';
import { Ajax } from '@shared/Ajax';
import { Events } from '@shared/Events';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import Feature, { FeatureLike } from 'ol/Feature';
import { transform as olTransform } from 'ol/proj';
import * as React from 'react';
import { mapRootStore, mapStation, mapTools, mapUI, mapWrapper } from '../map';
import { VectorTileMouseInteraction } from '@openlayers/interaction/VectorTileDesktopMouseInteraction';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';

export class DefaultCursorTool implements MapCursorTool {
    handleMapClick = (evt: ClickEventWithCoordinate) => {
        if (evt.shiftKey) return; // shift interferes with map drawing tools
        if (mapTools.activeTool != null) return;

        const pixel = mapWrapper.getInstance().getEventPixel(evt);
        const [e, n] = evt.coordinate;
        let topmostLayer = null;

        // clear selection, ui elements
        MapOverlays.hideAllOverlays(mapWrapper.getInstance());
        MapOverlays.hideForMapEvent(mapWrapper.getInstance(), 'click');

        // fire click handler on first clicked features
        let feature: Feature = mapWrapper.getInstance().forEachFeatureAtPixel(pixel, (feature, layer) => {
            if (layer && layer.get('id') !== 'extras' && layer.get('id') !== 'tool-draw') {
                topmostLayer = layer;
                return feature;
            }
        }) as Feature;

        // click on empty space
        const type: FeatureType = feature ? feature.get('type') : null;
        if (!feature || type === 'model' || type === 'mesh-image' || topmostLayer.get('id') === 'tool-draw') {
            // allow clicking inside model, IN-2495
            mapWrapper.clickMarkerLayer.drawClickMarker(e, n);
            if (document.querySelector('#map-photo-popup')) {
                ReactUtils.safeUnmountComponentAtNode(document.querySelector('#map-photo-popup'));
            }

            if (!feature) {
                Events.trigger('mapClickModel', { id: null, name: null, props: null });
                return;
            }
        }

        const cluster = isCluster(feature) ? feature : null;
        feature = declusterFeature(feature);

        const mouseInteraction: FeatureMouseInteraction = feature.get('mouseInteraction');
        if (mouseInteraction && typeof mouseInteraction.onClick === 'function') {
            if (type === 'model') {
                const modelFeatures = mapWrapper.getInstance().getFeaturesAtPixel(pixel, {
                    layerFilter: layer => layer.get('id') === 'model',
                });
                mouseInteraction.onClick(modelFeatures, e, n);
            } else {
                const eachFeatureAtPixel = [];
                mapWrapper.getInstance().forEachFeatureAtPixel(pixel, (f, layer) => {
                    if (layer && layer == topmostLayer) {
                        eachFeatureAtPixel.push(f);
                    }
                });
                mouseInteraction.onClick(cluster || feature, e, n, eachFeatureAtPixel);
            }
        }
    };

    mouseOverFeature: Feature = null;
    handleMapMouseMove = _.throttle(evt => {
        if (mapTools.activeTool != null) return;
        if (evt.dragging) return;
        if ((evt.originalEvent.target as HTMLElement).nodeName !== 'CANVAS') return;

        const pixel = mapWrapper.getInstance().getEventPixel(evt.originalEvent);

        if (!mapWrapper.getInstance().hasFeatureAtPixel(pixel)) {
            if (this.mouseOverFeature) {
                this.clearOverlays();
            }
            return;
        }

        let feature = mapWrapper.getInstance().getFeaturesAtPixel(pixel)[0] as Feature;

        if (MapOverlays.getDialogFeature() == feature) return; // don't show tooltip on opened feature

        const cluster = isCluster(feature) ? feature : null;
        feature = declusterFeature(feature);

        const mouseInteraction: FeatureMouseInteraction = feature.get('mouseInteraction');
        if (mouseInteraction && typeof mouseInteraction.onMouseOver === 'function') {
            if (this.mouseOverFeature !== feature && !feature.get('no_hover_style') /* perf optim, set() triggers redraw */) {
                if (this.mouseOverFeature) {
                    this.mouseOverFeature.set('hover', 0);
                }
                this.mouseOverFeature = feature;
                this.mouseOverFeature.set('hover', 1);
            }
            this.mouseOverFeature = feature;
            const [e, n] = evt.coordinate;
            mouseInteraction.onMouseOver(cluster || feature, e, n);
        } else {
            this.clearOverlays();
        }
    }, 16);

    clearOverlays() {
        if (this.mouseOverFeature !== null) {
            if (!this.mouseOverFeature.get('no_hover_style')) {
                this.mouseOverFeature.set('hover', 0);
            }
            this.mouseOverFeature = null;
        }

        mapWrapper.featureLayers.models.clearStyles();
        mapWrapper.featureLayers.pipes.clearStyles();
        mapWrapper.featureLayers.stringlines.clearStyles();
        mapWrapper.featureLayers.points.clearStyles();
        VectorTileMouseInteraction.clear();
        setMapCursor(mapWrapper.getInstance(), '');
        MapOverlays.hideTooltip(mapWrapper.getInstance());
    }

    handleMapRightClick = (evt: ClickEventWithCoordinate) => {
        if (evt.shiftKey) return true;
        evt.preventDefault();

        Telemetry.add(TelemetryEventType.MAP_CLICK_RIGHT, TelemetrySource.MAP_2D_DESKTOP);
        MapOverlays.hideForMapEvent(mapWrapper.getInstance(), 'right-click');

        const pixel = mapWrapper.getInstance().getEventPixel(evt);
        const coord = mapWrapper.getInstance().getEventCoordinate(evt);
        const [e, n] = coord;
        let [lon, lat] = olTransform(coord, 'EPSG:3857', 'EPSG:4326');

        let html = "<div class='context-menu card'>";
        html += "<i class='iki iki-cross close abs' onclick='mapUI.hideContextMenu()'></i>";
        html += '<div class="context-menu-body dropdown-react-wrapper" style="font-size: 12px;">';
        html += "<ul class='images-cluster-list clickable'>";

        // get topmost feature
        let cluster: FeatureLike;
        const feature: Feature = mapWrapper.getInstance().forEachFeatureAtPixel(pixel, (feature, layer) => {
            const id = layer?.get('id');
            if (id) {
                if (id === 'image' || id === 'document') {
                    cluster = isCluster(feature) ? feature : null;
                    return declusterFeature(feature as Feature);
                } else if (id !== 'extras') {
                    return feature;
                }
            }
        }) as Feature;

        let type: FeatureType = null;
        let id = null;

        let targetFeature = feature;
        if (feature) {
            type = feature.get('type');
            id = feature.get('id');
            const source = feature.get('source');
            if (!id && source) {
                id = source.modelId;
            }
            if (!type && feature.get('measurementFeature')) {
                targetFeature = feature.get('measurementFeature') as Feature;
                type = targetFeature.get('type') as FeatureType;
            }
        }

        if (type === 'area-measurement' || type === 'distance-measurement' || type === 'volume-measurement') {
            openMapToolContextMenu(pixel, targetFeature, type);
            return;
        }

        mapWrapper.clickMarkerLayer.drawClickMarker(e, n);

        if (type && type !== 'logpoint') {
            if ((type === 'model' || type == 'mesh-image') && $.isNumeric(id)) {
                const toggleRasterizationMsg = mapWrapper.backgroundMaps.getRasterizedModelIds().has(id)
                    ? kuura.getMessage('js_map.hide_triangle_mesh')
                    : kuura.getMessage('js_map.show_triangle_mesh');
                html += `<li onclick='mapWrapper.backgroundMaps.toggleRasterizedModelId(${id})'>${toggleRasterizationMsg}</li>`;

                const toggleStringlinesMsg = mapWrapper.featureLayers.stringlines.overlayModelFeaturesById[id]
                    ? kuura.getMessage('js_map.hide_stringlines')
                    : kuura.getMessage('js_map.show_stringlines');
                html += `<li onclick='mapWrapper.featureLayers.stringlines.toggleOverlayModel(${id})'>${toggleStringlinesMsg}</li>`;
            }

            if (type === 'model' || type === 'stringline' || type === 'model_point' || type === 'alignment' || type === 'pipe' || type == 'mesh-image') {
                Telemetry.add(TelemetryEventType.MAP_CLICK_RIGHT_MODEL, TelemetrySource.MAP_2D_DESKTOP);

                if (type !== 'pipe') {
                    html += `<li onclick='mapWrapper.fitFeatureById(${id})'>${kuura.getMessage('js_map.fit_into_view')}</li>`;
                }
                html += `<li onclick='mapUI.mapPanels.focusFeatureInTree(${id})'>${kuura.getMessage('js_map.select_in_files_tree')}</li>`;
                if (kuura.hasPrivilege('MODEL_PAGE')) {
                    html += `<li onclick='mapUI.mapPanels.openInFilesPage(${id})'>${kuura.getMessage('js_menu.open_in_files_page')}</li>`;
                    if (kuura.hasPrivilege('Download_Model')) {
                        html += `<li onclick='mapUI.mapPanels.downloadModel(${id})'>${kuura.getMessage(
                            'js_files.download_btn'
                        )}&nbsp <i class='iki iki-download' style='font-size: 13px'></i></li>`;
                    }
                }
                html += `<li onclick='mapUI.mapPanels.panels.logpoints.genericComponent.tree.checkIds([${id}])'>${kuura.getMessage('js_map.load_attached_logpoints')}</li>`;
            }

            if (kuura.hasPrivilege('TRUCKS_PAGE') && type == 'model') {
                html += `<li onclick='mapTools.convertModelToGeofence(${id}, ${n}, ${e})'>${kuura.getMessage('trucks.create_geofence')}</li>`;
            }

            if (type == 'image') {
                const image: IImage = feature.get('data');
                lat = image.geographicPoint.lat;
                lon = image.geographicPoint.lon;
                html += `<li onclick='mapUI.mapPanels.panels.images.showInPanel(${image.id})'>${kuura.getMessage('js_map.show_in_panel')}</li>`;
                if (!cluster && kuura.hasPrivilege('Download_Image')) {
                    html += `<li onclick='mapUI.mapPanels.downloadModel(${image.id})'>${kuura.getMessage(
                        'js_files.download_btn'
                    )}&nbsp <i class='iki iki-download' style='font-size: 13px'></i></li>`;
                }
            }

            if (type == 'model' || type == 'alignment' || type == 'stringline' || type == 'model_point' || type == 'pipe') {
                const { element, overlay } = MapOverlays.addOverlay(mapWrapper.getInstance(), {
                    positioning: 'bottom-left',
                    position: [e, n],
                    offset: [7, -7],
                    hideOn: {
                        rightClick: true,
                        click: true,
                    },
                });

                ReactUtils.safeRender(React.createElement(GanttPopup, { modelId: id }), element);
            }
        }

        // clicked on empty space or logpoint/model, draw station line, add cross section button
        if (!type || type == 'logpoint' || type == 'model' || type == 'mesh-image') {
            const activeAlignment = mapWrapper.featureLayers.alignments.getActiveAlignment();
            if (mapWrapper.featureLayers.alignments.activeAlignmentFeature) {
                const station = mapUI.stationAndModelInfoBox.stationInfo.station;
                mapStation.fetchAndDrawStationLine(e, n, activeAlignment.modelId, activeAlignment.index);
                html += `<li onclick="mapUI.openCrossSectionWindowForCurrentStation()"><i class="iki iki-cross-section"></i> <span>${kuura.getMessage(
                    'js_tablet.draw_cross_section'
                )}</span></li>`;
                html += `<li onclick="mapTools.openLongSectionWindow(${id})"><i class="iki iki-long-section"></i> <span>${kuura.getMessage(
                    'js_tablet.draw_long_section'
                )}</span></li>`;
            } else {
                html += `<div style="padding: 17px 34px 17px 17px; max-width: 230px">${kuura.getMessage('js_map.no_active_alignment_for_crossection')}</div>`;
            }

            html += `<li onclick="mapUI.createLogpoint(${lat}, ${lon})">${kuura.getMessage('js_map.create_logpoint')}</li>`;
        }

        html += `<li id='js-map__context__nearby-models-btn' style='display: none'></li>`;
        html += `<li onclick='window.open("http://maps.google.com/?q=${lat},${lon}")'>${kuura.getMessage('js_map.open_in_google_maps')}</li>`;

        html += '</ul>';
        html += '</div>';

        html += "<div class='context-menu-position'>";
        html += 'lat: ' + lat.toFixed(7) + '&nbsp;&nbsp;&nbsp lon: ' + lon.toFixed(7);
        html += "<br>n:&nbsp&nbsp <span id='context-menu-n'></span>";
        html += "&nbsp;&nbsp;&nbsp e:&nbsp&nbsp <span id='context-menu-e'></span>";
        html += '<br></span>';
        html += '</div>';

        html += '</div>';

        MapOverlays.showContextMenu(mapWrapper.getInstance(), html, e, n);

        Ajax({
            url: 'api/1/project/' + kuura.getProjectId() + '/convert_lat_lon.json',
            data: {
                lat,
                lon,
            },
        }).done(resp => {
            $('#context-menu-n').html(resp.coords.n.toFixed(2));
            $('#context-menu-e').html(resp.coords.e.toFixed(2));
        });

        // nearby models
        const nearbyModelsBtn = document.getElementById('js-map__context__nearby-models-btn');
        if (nearbyModelsBtn) {
            nearbyModelsBtn.style.display = 'block';
            nearbyModelsBtn.className = 'loading-inline';
            nearbyModelsBtn.innerHTML = `${kuura.getMessage('js_map.nearby_models.title')}`;

            NearbyModelsService.fetch(lat, lon).then(resp => {
                nearbyModelsBtn.style.display = 'block';
                nearbyModelsBtn.className = '';
                nearbyModelsBtn.innerHTML = `${resp.matchingModels.length} ${kuura.getMessage('js_map.models_in_this_location')}<br>${resp.nearByModels.length} ${kuura.getMessage(
                    'js_map.nearby_models'
                )}`;
                nearbyModelsBtn.addEventListener('click', () =>
                    NearbyModelsDialog.open({
                        models: resp,
                        treeData: mapRootStore.treeData,
                        onLoad: (ids, includeLogpoints) => {
                            mapUI.mapPanels.activatePanelById('models');
                            mapUI.mapPanels.panels.models.checkIds(ids);
                            if (includeLogpoints) {
                                mapUI.mapPanels.panels.logpoints.genericComponent.tree.checkIds(ids);
                            }
                            NearbyModelsDialog.close();
                        },
                    })
                );
            });
        }
    };

    handleMapZoom = () => {
        const zoom = mapWrapper.getInstance().getView().getZoom() || 12;
        MapOverlays.hideForMapEvent(mapWrapper.getInstance(), 'zoom');

        if (mapWrapper.featureLayers.alignments) {
            mapWrapper.featureLayers.alignments.onMapZoom(zoom);
        }
    };
}
